import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Search, Plus, Edit, Trash2, UserCheck, UserX, Upload, Users, UserPlus, Activity, Building, RefreshCw } from 'lucide-react';
import {
  getUsers,
  createUser,
  updateUser,
  deleteUser,
  inviteSingle,
  inviteBulk,
  assignAssessment,
  assignAssessmentBulk
} from '../api/users';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/Button';
import { Card, CardContent } from '../components/ui/Card';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import StatCard from '../components/charts/StatCard';
import PieChart from '../components/charts/PieChart';
import BarChart from '../components/charts/BarChart';
import AddUserDrawer from '../components/AddUserDrawer';
import BulkUploadModal from '../components/BulkUploadModal';
import toast from 'react-hot-toast';

const UserManagementPageEnhanced = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [showAddDrawer, setShowAddDrawer] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({ email: '', displayName: '', role: 'user' });
  const [actionLoading, setActionLoading] = useState({});
  const [inviteLoading, setInviteLoading] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [customerInfo, setCustomerInfo] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [analytics, setAnalytics] = useState(null);

  const loadUsers = async (searchTerm = search, currentPage = page, isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    }
    try {
      setLoading(true);
      const response = await getUsers(currentPage, 20, searchTerm);
      setUsers(response.data || []);
      setTotal(response.total || 0);
      setAnalytics(response.analytics || null);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Failed to load users:', error);
      
      // Handle different error types
      if (error.response?.status === 401) {
        toast.error('Please sign in to access user management');
        setTimeout(() => {
          window.location.href = '/login';
        }, 1000);
        return;
      } else if (error.response?.status === 404) {
        toast.error('User management endpoint not found. Please check backend configuration.');
      } else if (error.code === 'ECONNREFUSED' || error.message.includes('Network Error')) {
        toast.error('Cannot connect to server. Please ensure the backend is running on port 5000.');
      } else {
        toast.error('Failed to load users. Please try again.');
      }
      
      // Set empty state on error to prevent crashes
      setUsers([]);
      setTotal(0);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };
  
  const handleRefresh = () => {
    loadUsers(search, page, true);
  };

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setPage(1);
      loadUsers(search, 1);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [search]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        await updateUser(editingUser.id, formData);
        toast.success('User updated successfully');
      } else {
        await createUser(formData);
        toast.success('User created successfully');
      }
      setShowModal(false);
      setEditingUser(null);
      setFormData({ email: '', displayName: '', role: 'user' });
      loadUsers();
    } catch (error) {
      console.error('Failed to save user:', error);
      toast.error('Failed to save user');
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      displayName: user.display_name || '',
      role: user.role
    });
    setShowModal(true);
  };

  const handleToggleActive = async (user) => {
    const actionKey = `toggle-${user.id}`;
    setActionLoading(prev => ({ ...prev, [actionKey]: true }));
    
    try {
      await updateUser(user.id, { isActive: !user.is_active });
      toast.success(`User ${user.is_active ? 'deactivated' : 'activated'} successfully`);
      await loadUsers();
    } catch (error) {
      console.error('Failed to toggle user status:', error);
      toast.error('Failed to update user status');
    } finally {
      setActionLoading(prev => ({ ...prev, [actionKey]: false }));
    }
  };

  const handleDelete = async (user) => {
    if (!confirm(`Are you sure you want to delete ${user.email}?`)) return;
    
    const actionKey = `delete-${user.id}`;
    setActionLoading(prev => ({ ...prev, [actionKey]: true }));
    
    try {
      await deleteUser(user.id);
      toast.success('User deleted successfully');
      await loadUsers();
    } catch (error) {
      console.error('Failed to delete user:', error);
      toast.error('Failed to delete user');
    } finally {
      setActionLoading(prev => ({ ...prev, [actionKey]: false }));
    }
  };

  const handleInviteSingle = async (userData) => {
    setInviteLoading(true);
    try {
      const isAssessmentUser = String(userData.role || '').toLowerCase() === 'user';
      const result = isAssessmentUser
        ? await assignAssessment(userData)
        : await inviteSingle(userData);

      toast.success(result.message || 'Invitation sent successfully');
      setShowAddDrawer(false);
      await loadUsers();
    } catch (error) {
      console.error('Failed to invite user:', error);
      toast.error(error.response?.data?.error || 'Failed to invite user');
      throw error;
    } finally {
      setInviteLoading(false);
    }
  };

  const handleBulkUpload = async (payload) => {
    setBulkLoading(true);
    try {
      const result = Array.isArray(payload)
        ? await assignAssessmentBulk(payload)
        : await inviteBulk(payload);
      await loadUsers();
      return result;
    } catch (error) {
      console.error('Failed to process bulk upload:', error);
      toast.error(error.response?.data?.error || 'Failed to process bulk upload');
      throw error;
    } finally {
      setBulkLoading(false);
    }
  };

  // Get customer info for display - MUST be before early returns
  useEffect(() => {
    if (users.length > 0 && currentUser?.role !== 'admin') {
      const userWithCustomer = users.find(u => u.customer_name);
      if (userWithCustomer && !customerInfo) {
        setCustomerInfo({ name: userWithCustomer.customer_name });
      }
    }
  }, [users, currentUser?.role, customerInfo]);

  // Calculate analytics with defensive checks
  const safeUsers = Array.isArray(users) ? users : [];
  const activeUsers = safeUsers.filter(u => u && u.is_active).length;
  const inactiveUsers = safeUsers.filter(u => u && !u.is_active).length;
  const roleDistribution = safeUsers.reduce((acc, user) => {
    if (user && user.role) {
      acc[user.role] = (acc[user.role] || 0) + 1;
    }
    return acc;
  }, {});

  const roleChartData = Object.entries(roleDistribution).map(([role, count]) => ({
    name: role.toUpperCase(),
    value: count
  }));

  const activityData = [
    { name: 'Active', value: activeUsers },
    { name: 'Inactive', value: inactiveUsers }
  ];

  // Show loading state
  if (loading && users.length === 0) {
    return (
      <div className="flex items-center justify-center py-20">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Show error state if no users and not loading
  if (!loading && users.length === 0 && total === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="text-gray-500 mb-4">
          <Users className="w-16 h-16 mx-auto mb-4" />
          <p className="text-lg font-medium">No users found</p>
          <p className="text-sm">The user management service may not be available.</p>
        </div>
        <Button onClick={() => loadUsers()} className="mt-4">
          Retry
        </Button>
      </div>
    );
  }

  // Filter available roles based on current user
  const getAvailableRoles = () => {
    if (currentUser?.role === 'admin') {
      return ['user', 'hr', 'customer_admin', 'admin'];
    }
    if (currentUser?.role === 'customer_admin') {
      return ['user', 'hr'];
    }
    return ['user'];
  };

  return (
    <>
      {/* Header with Customer Context */}
      <motion.div 
        className="mb-8"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">User Management</h1>
            <div className="flex items-center space-x-4">
              <p className="text-gray-600">
                {currentUser?.role === 'admin' 
                  ? 'Manage all users across all organizations' 
                  : 'Manage users in your organization'
                }
              </p>
              {customerInfo && currentUser?.role !== 'admin' && (
                <div className="flex items-center space-x-2 bg-blue-50 px-3 py-1 rounded-full">
                  <Building className="w-4 h-4 text-blue-600" />
                  <span className="text-sm font-medium text-blue-800">{customerInfo.name}</span>
                </div>
              )}
              {lastUpdated && (
                <span className="text-sm text-gray-500">
                  Last updated: {lastUpdated.toLocaleTimeString()}
                </span>
              )}
            </div>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            <span>{refreshing ? 'Refreshing...' : 'Refresh'}</span>
          </button>
        </div>
      </motion.div>

      {/* Analytics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <StatCard
            icon={Users}
            label="Total Users"
            value={total}
            trend={analytics?.trends?.total}
            color="blue"
          />
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <StatCard
            icon={UserCheck}
            label="Active Users"
            value={activeUsers}
            trend={analytics?.trends?.active}
            color="green"
          />
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
          <StatCard
            icon={UserX}
            label="Inactive Users"
            value={inactiveUsers}
            trend={analytics?.trends?.inactive}
            color="orange"
          />
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
          <StatCard
            icon={UserPlus}
            label="New (7d)"
            value={analytics?.new7d || 0}
            trend={analytics?.trends?.new7d}
            color="purple"
          />
        </motion.div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Role Distribution */}
        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.5 }}>
          <Card>
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">Users by Role</h3>
              <div className="flex items-center justify-center">
                {roleChartData.length > 0 ? (
                  <div className="w-full max-w-sm">
                    <PieChart 
                      data={roleChartData}
                      colors={['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6']}
                      height={240}
                    />
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-64 text-gray-500">
                    No data available
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Activity Status */}
        <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.6 }}>
          <Card>
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
                <Activity className="w-5 h-5 mr-2" />
                User Activity Status
              </h3>
              <BarChart
                data={activityData}
                dataKeys={[{ dataKey: 'value', name: 'Users' }]}
                colors={['#10b981']}
                height={240}
              />
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Search and Add User */}
      <Card className="mb-6">
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="relative w-full sm:flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search by email or name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="flex flex-wrap gap-3 w-full sm:w-auto">
              <Button
                onClick={() => setShowAddDrawer(true)}
                className="flex items-center space-x-2"
              >
                <Plus className="w-4 h-4" />
                <span>Add User</span>
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowBulkModal(true)}
                className="flex items-center space-x-2"
              >
                <Upload className="w-4 h-4" />
                <span>Bulk Upload</span>
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Login</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {safeUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{user.display_name || user.email.split('@')[0]}</div>
                        <div className="text-sm text-gray-500">{user.email}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-500">
                        {user.phone || 'Not provided'}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        user.role === 'admin' ? 'bg-red-100 text-red-800' :
                        user.role === 'customer_admin' ? 'bg-purple-100 text-purple-800' :
                        user.role === 'hr' ? 'bg-blue-100 text-blue-800' :
                        'bg-green-100 text-green-800'
                      }`}>
                        {user.role.replace('_', ' ').toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        user.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {user.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEdit(user)}
                          className="flex items-center space-x-1"
                        >
                          <Edit className="w-3 h-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleToggleActive(user)}
                          loading={actionLoading[`toggle-${user.id}`]}
                          className="flex items-center space-x-1"
                        >
                          {user.is_active ? <UserX className="w-3 h-3" /> : <UserCheck className="w-3 h-3" />}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDelete(user)}
                          loading={actionLoading[`delete-${user.id}`]}
                          className="flex items-center space-x-1 text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {safeUsers.length === 0 && !loading && (
                  <tr>
                    <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                      No users found. Try adjusting your search or check if the backend service is running.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Edit User Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">
              {editingUser ? 'Edit User' : 'Add User'}
            </h3>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    required
                    disabled={editingUser}
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Display Name</label>
                  <input
                    type="text"
                    value={formData.displayName}
                    onChange={(e) => setFormData(prev => ({ ...prev, displayName: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                  <select
                    required
                    value={formData.role}
                    onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {getAvailableRoles().map(role => (
                      <option key={role} value={role}>
                        {role.charAt(0).toUpperCase() + role.slice(1).replace('_', ' ')}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </Button>
                <Button type="submit">
                  {editingUser ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add User Drawer */}
      <AddUserDrawer
        isOpen={showAddDrawer}
        onClose={() => setShowAddDrawer(false)}
        onSubmit={handleInviteSingle}
        loading={inviteLoading}
      />

      {/* Bulk Upload Modal */}
      <BulkUploadModal
        isOpen={showBulkModal}
        onClose={() => setShowBulkModal(false)}
        onSubmit={handleBulkUpload}
        loading={bulkLoading}
      />
    </>
  );
};

export default UserManagementPageEnhanced;
